/**
 * Customer Context
 * Manages selected customer/condo state and user's customer bindings
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Customer } from '../types/customer';
import { customerService } from '../services/customerService';
import { useKeycloak } from './KeycloakContext';
import { isAdmin } from '../config/roles';
import { logger } from '../services/logger';
import { fetchWithAuth } from '../services/api';

// One (customer, role) pair from a non-admin user's active UserCustomerAssignment rows.
export interface AccessChoice {
  customer: Customer;
  role: string;
}

// Per-customer assignment roles that do NOT count as "operational" access — a user whose
// active assignments are ALL morador/funcionario has no reason to use the desktop app at
// all (that's what the mobile app is for), and never triggers the role picker either.
const NON_OPERATIONAL_ROLES = new Set(['morador', 'funcionario']);

interface CustomerContextType {
  // Current selected customer (for admins) or assigned customer (for non-admins)
  selectedCustomer: Customer | null;

  // List of all available customers. For admins, all customers. For non-admins, the
  // customers matching activeRole (or all operational customers when there's only one
  // distinct role — the role concept doesn't apply, so nothing is filtered out).
  availableCustomers: Customer[];

  // Select a customer (admin only) - returns success/error status
  // Optional callback is called after state update is flushed (for navigation)
  selectCustomer: (
    customerId: string | null,
    options?: { onComplete?: () => void }
  ) => Promise<{ success: boolean; error?: string }>;

  // Clear saved customer selection from localStorage
  clearSavedSelection: () => void;

  // Refresh the customers list (call after creating/updating customers)
  refreshCustomers: () => Promise<void>;

  // User's assigned customer ID (null for admins)
  userAssignedCustomerId: string | null;

  // Whether user is bound to a specific customer
  isUserBound: boolean;

  // Loading state
  isLoading: boolean;

  // True once customer selection logic is complete (prevents premature dashboard renders)
  isInitialized: boolean;

  // True when a non-admin user's active assignments are ALL morador/funcionario (or they
  // have none at all) — desktop has no operational use for this account. Admins are never
  // blocked. The app should render DesktopNotAvailable instead of the normal tree.
  desktopAccessBlocked: boolean;

  // Every distinct operational role the user holds across all their assignments (e.g.
  // ['porteiro', 'sindico']). Length <= 1 means the role concept is moot — the customer
  // switcher alone is enough, no role picker/button is ever needed. Length > 1 is what
  // gates pendingRoleChoice and should show a "switch role" affordance somewhere in nav.
  operationalRoles: string[];

  // The role the user is currently operating as (null only while a choice is pending, or
  // for admins/users with a single role where the concept is irrelevant but harmless).
  activeRole: string | null;

  // True when the user has more than one operationalRole and hasn't picked one yet (or
  // explicitly reopened the picker via openRolePicker) — render AccessPicker instead of
  // the normal app tree.
  pendingRoleChoice: boolean;

  // Pick a role: filters availableCustomers down to that role's customers, auto-selects
  // if there's exactly one, otherwise restores a previously-saved customer within that
  // role or leaves selection to the normal customer switcher. Persists the choice.
  chooseRole: (role: string) => void;

  // Reopen the role picker (e.g. a "trocar papel" button in Layout) without losing the
  // current selection until the user actually picks a (possibly different) role.
  openRolePicker: () => void;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

// LocalStorage key prefix - combined with user ID for user-specific storage
const STORAGE_KEY_PREFIX = 'selectedCustomer_';
const STORAGE_EXPIRY_DAYS = 30;

// Helper to get user-specific storage key
const getStorageKey = (userId: string): string => `${STORAGE_KEY_PREFIX}${userId}`;

interface CustomerSelection {
  customerId: string;
  customerName: string;
  timestamp: number;
  userId: string; // Track which user this selection belongs to
}

// Helper functions for localStorage - now user-specific
const saveCustomerSelection = (customer: Customer, userId: string): void => {
  try {
    const selection: CustomerSelection = {
      customerId: customer.id,
      customerName: customer.name,
      timestamp: Date.now(),
      userId,
    };
    localStorage.setItem(getStorageKey(userId), JSON.stringify(selection));
    logger.debug('customer.context', 'Saved customer selection to localStorage', { customerName: customer.name, userId });
  } catch (error) {
    logger.error('customer.context', 'Failed to save customer selection', error);
  }
};

const loadCustomerSelection = (userId: string): CustomerSelection | null => {
  try {
    const storageKey = getStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;

    const selection: CustomerSelection = JSON.parse(stored);

    // Verify this selection belongs to the current user (extra safety check)
    if (selection.userId && selection.userId !== userId) {
      logger.warn('customer.context', 'Stored selection belongs to different user, clearing');
      localStorage.removeItem(storageKey);
      return null;
    }

    // Check if older than expiry days
    const expiryMs = STORAGE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - selection.timestamp > expiryMs) {
      logger.debug('customer.context', 'Saved customer selection expired, clearing');
      localStorage.removeItem(storageKey);
      return null;
    }

    logger.debug('customer.context', 'Loaded customer selection from localStorage', { customerName: selection.customerName, userId });
    return selection;
  } catch (error) {
    logger.error('customer.context', 'Failed to load customer selection', error);
    return null;
  }
};

const clearCustomerSelection = (userId: string): void => {
  try {
    localStorage.removeItem(getStorageKey(userId));
    logger.debug('customer.context', 'Cleared customer selection from localStorage', { userId });
  } catch (error) {
    logger.error('customer.context', 'Failed to clear customer selection', error);
  }
};

// Persists which operational role (porteiro/sindico/etc.) a multi-role user last chose to
// operate as, so the role picker only needs to be shown once per role switch, not on
// every app load — mirrors saveCustomerSelection/loadCustomerSelection above.
const ROLE_STORAGE_KEY_PREFIX = 'activeRole_';
const getRoleStorageKey = (userId: string): string => `${ROLE_STORAGE_KEY_PREFIX}${userId}`;

const saveActiveRole = (role: string, userId: string): void => {
  try {
    localStorage.setItem(getRoleStorageKey(userId), role);
  } catch (error) {
    logger.error('customer.context', 'Failed to save active role', error);
  }
};

const loadActiveRole = (userId: string): string | null => {
  try {
    return localStorage.getItem(getRoleStorageKey(userId));
  } catch (error) {
    logger.error('customer.context', 'Failed to load active role', error);
    return null;
  }
};

// Clean up old legacy storage key (without user ID) if it exists
const cleanupLegacyStorage = (): void => {
  try {
    localStorage.removeItem('selectedCustomer');
  } catch (error) {
    // Ignore errors during cleanup
  }
};

/**
 * Transform customer data from API to include top-level condoType field
 * Extracts condo_type from condo_data JSONB field for condominios
 */
const transformCustomer = (customer: any): Customer => {
  // Normalize snake_case API fields to camelCase
  const base = {
    ...customer,
    postalCode: customer.postalCode || customer.postal_code,
  };

  if (customer.type === 'condominio') {
    // Extract condo_type from condo_data JSONB field
    const condoType = customer.condo_data?.condo_type || customer.condoType || 'apartamento';
    return {
      ...base,
      condoType
    } as Customer;
  }
  return base as Customer;
};

export function CustomerProvider({ children }: { children: ReactNode }) {
  const { user, roles, token } = useKeycloak();
  const queryClient = useQueryClient();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [availableCustomers, setAvailableCustomers] = useState<Customer[]>([]);
  const [userAssignedCustomerId, setUserAssignedCustomerId] = useState<string | null>(null);
  const [isUserBound, setIsUserBound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [desktopAccessBlocked, setDesktopAccessBlocked] = useState(false);
  const [operationalRoles, setOperationalRoles] = useState<string[]>([]);
  const [operationalAssignments, setOperationalAssignments] = useState<AccessChoice[]>([]);
  const [activeRole, setActiveRoleState] = useState<string | null>(null);
  const [pendingRoleChoice, setPendingRoleChoice] = useState(false);

  const userIsAdmin = isAdmin(roles);

  // Filters availableCustomers/selectedCustomer down to one role's customers, auto-selecting
  // when there's exactly one, else restoring a previously-saved customer within that role's
  // subset (or leaving it to the customer switcher). Shared by the initial-load branch and
  // the user-triggered chooseRole() below so both apply the identical selection rule.
  const applyRole = (role: string, choices: AccessChoice[], userId: string) => {
    const customersForRole = Array.from(
      new Map(choices.filter(c => c.role === role).map(c => [c.customer.id, c.customer])).values()
    );
    setAvailableCustomers(customersForRole);
    setActiveRoleState(role);
    setUserAssignedCustomerId(customersForRole.length === 1 ? customersForRole[0].id : null);
    setIsUserBound(true);

    if (customersForRole.length === 1) {
      setSelectedCustomer(customersForRole[0]);
      saveCustomerSelection(customersForRole[0], userId);
      return;
    }
    const savedSelection = loadCustomerSelection(userId);
    const savedCustomer = savedSelection ? customersForRole.find(c => c.id === savedSelection.customerId) : null;
    setSelectedCustomer(savedCustomer ?? null);
  };

  const chooseRole = (role: string) => {
    if (!user) return;
    saveActiveRole(role, user.id);
    setPendingRoleChoice(false);
    applyRole(role, operationalAssignments, user.id);
    logger.info('customer.context', 'User chose active role', { role });
  };

  const openRolePicker = () => {
    setPendingRoleChoice(true);
    logger.info('customer.context', 'Role picker reopened');
  };

  useEffect(() => {
    const loadCustomers = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      logger.info('customer.context', 'Initializing customer context', {
        username: user.username,
        isAdmin: userIsAdmin
      });

      // Clean up old legacy storage (from before user-specific keys)
      cleanupLegacyStorage();

      try {
        if (userIsAdmin) {
          // Admins are never gated by desktopAccessBlocked/pendingRoleChoice — reset in
          // case this effect re-ran after a prior non-admin state (e.g. user switch).
          setDesktopAccessBlocked(false);
          setPendingRoleChoice(false);
          setOperationalRoles([]);
          setOperationalAssignments([]);
          setActiveRoleState(null);

          // Admin: Try to load from API, fallback to mock data if empty or fails
          try {
            const response = await customerService.getAllCustomers();
            const rawCustomers = response.data || [];

            // Transform customers to extract condoType from condo_data
            const customers = rawCustomers.map(transformCustomer);

            // Use data from API
            setAvailableCustomers(customers);
            setUserAssignedCustomerId(null);
            setIsUserBound(false);
            logger.info('customer.context', 'Admin loaded customers from API', { count: customers.length });

            // Try to restore admin's last selected customer from localStorage
            const savedSelection = loadCustomerSelection(user.id);
            if (savedSelection) {
              logger.info('customer.context', 'Found saved selection for admin, attempting restore', {
                savedCustomerId: savedSelection.customerId,
                savedCustomerName: savedSelection.customerName
              });

              // Validate saved customer still exists
              const savedCustomer = customers.find(c => c.id === savedSelection.customerId);
              if (savedCustomer) {
                logger.info('customer.context', 'Restored admin customer selection', { name: savedCustomer.name });
                setSelectedCustomer(savedCustomer);
              } else {
                logger.warn('customer.context', 'Saved customer no longer exists, clearing selection');
                clearCustomerSelection(user.id);
                setSelectedCustomer(null);
              }
            } else {
              logger.info('customer.context', 'No saved selection found, starting at General View');
              setSelectedCustomer(null);
            }
          } catch (apiError) {
            logger.error('customer.context', 'Admin failed to load customers from API', apiError);
            // No fallback to mock data - show error
            setAvailableCustomers([]);
            setSelectedCustomer(null);
            setUserAssignedCustomerId(null);
            setIsUserBound(false);
          }
        } else {
          // Non-admin: Load only assigned customers based on user assignments
          try {
            logger.debug('customer.context', 'Fetching user assignments', {
              userId: user.id
            });

            // Fetch user assignments from backend
            const response = await fetchWithAuth(`/api/v1/users/${user.id}/assignments`);

            if (!response.ok) {
              const error = new Error(`Failed to fetch user assignments: ${response.status}`);
              logger.error('customer.context', 'Failed to fetch assignments', error, { status: response.status });
              throw error;
            }

            const assignmentsData = await response.json();
            const assignments = assignmentsData.data || [];

            logger.debug('customer.context', 'Fetched user assignments', { count: assignments.length });

            // Filter active assignments and extract unique customer IDs
            const activeAssignments = assignments.filter((a: any) => a.is_active);
            const assignedCustomerIds = Array.from(new Set(activeAssignments.map((a: any) => a.customer_id)));

            logger.debug('customer.context', 'Processed assignments', {
              activeCount: activeAssignments.length,
              assignedCustomerIds: assignedCustomerIds
            });

            if (assignedCustomerIds.length === 0) {
              // User has no assigned customers at all — desktop has nothing to show.
              setAvailableCustomers([]);
              setSelectedCustomer(null);
              setUserAssignedCustomerId(null);
              setIsUserBound(false);
              setDesktopAccessBlocked(true);
              setOperationalRoles([]);
              setOperationalAssignments([]);
              setActiveRoleState(null);
              setPendingRoleChoice(false);
              logger.info('customer.context', 'No active customer assignments for non-admin user');
            } else {
              // Fetch each assigned customer individually — non-admin users
              // don't have permission to call GET /api/v1/customers (list all).
              const customerPromises = (assignedCustomerIds as string[]).map(id =>
                customerService.getCustomerById(id)
              );
              const results = await Promise.allSettled(customerPromises);
              const assignedCustomers = results.flatMap(r =>
                r.status === 'fulfilled' && r.value ? [transformCustomer(r.value)] : []
              );

              logger.debug('customer.context', 'Fetched assigned customers individually', { count: assignedCustomers.length });

              // Reduce to operational (customer, role) pairs — morador/funcionario-only
              // assignments don't belong on desktop at all (mobile is for that). A user can
              // have multiple roles at the same customer or the same role at multiple
              // customers; each distinct pair is its own choice.
              const customerById = new Map(assignedCustomers.map(c => [c.id, c]));
              const seenPairs = new Set<string>();
              const operationalChoices: AccessChoice[] = [];
              for (const a of activeAssignments as any[]) {
                if (NON_OPERATIONAL_ROLES.has(a.role)) continue;
                const customer = customerById.get(a.customer_id);
                if (!customer) continue;
                const key = `${a.customer_id}:${a.role}`;
                if (seenPairs.has(key)) continue;
                seenPairs.add(key);
                operationalChoices.push({ customer, role: a.role });
              }
              const distinctRoles = Array.from(new Set(operationalChoices.map(c => c.role)));
              setOperationalAssignments(operationalChoices);
              setOperationalRoles(distinctRoles);

              if (operationalChoices.length === 0) {
                // Every active assignment is morador/funcionario — this account has no
                // reason to use the desktop app.
                setAvailableCustomers([]);
                setSelectedCustomer(null);
                setUserAssignedCustomerId(null);
                setIsUserBound(false);
                setDesktopAccessBlocked(true);
                setActiveRoleState(null);
                setPendingRoleChoice(false);
                logger.info('customer.context', 'Non-admin user has only morador/funcionario assignments — desktop access blocked');
              } else {
                setDesktopAccessBlocked(false);

                if (distinctRoles.length <= 1) {
                  // Only one distinct operational role (regardless of how many customers) —
                  // the role concept doesn't apply. Behave exactly like before: auto-select
                  // if there's a single customer, else restore a saved one or leave it to
                  // the existing customer switcher. No role picker, ever.
                  const role = distinctRoles[0];
                  setActiveRoleState(role);
                  setPendingRoleChoice(false);
                  const operationalCustomers = Array.from(
                    new Map(operationalChoices.map(c => [c.customer.id, c.customer])).values()
                  );
                  setAvailableCustomers(operationalCustomers);

                  if (operationalCustomers.length === 1) {
                    setSelectedCustomer(operationalCustomers[0]);
                    setUserAssignedCustomerId(operationalCustomers[0].id);
                    setIsUserBound(true);
                    logger.info('customer.context', 'Auto-selected single operational assignment', {
                      customerName: operationalCustomers[0].name,
                      role
                    });
                  } else {
                    const savedSelection = loadCustomerSelection(user.id);
                    const savedCustomer = savedSelection
                      ? operationalCustomers.find(c => c.id === savedSelection.customerId)
                      : null;
                    setSelectedCustomer(savedCustomer ?? null);
                    setUserAssignedCustomerId(null);
                    setIsUserBound(true);
                    logger.info('customer.context', 'Single-role user can choose among multiple customers', {
                      role,
                      count: operationalCustomers.length
                    });
                  }
                } else {
                  // Multiple distinct operational roles (e.g. porteiro at one building,
                  // sindico at another). Restore a previously-chosen role if still valid;
                  // otherwise show the role picker — the user picks a ROLE, not a specific
                  // customer, and the customer switcher then operates within that role.
                  const savedRole = loadActiveRole(user.id);
                  if (savedRole && distinctRoles.includes(savedRole)) {
                    applyRole(savedRole, operationalChoices, user.id);
                    setPendingRoleChoice(false);
                    logger.info('customer.context', 'Restored saved active role', { role: savedRole });
                  } else {
                    setAvailableCustomers([]);
                    setSelectedCustomer(null);
                    setUserAssignedCustomerId(null);
                    setIsUserBound(true);
                    setActiveRoleState(null);
                    setPendingRoleChoice(true);
                    logger.info('customer.context', 'Multiple operational roles — showing role picker', {
                      roles: distinctRoles
                    });
                  }
                }
              }
            }
          } catch (error) {
            logger.error('customer.context', 'Failed to load user assignments', error);
            setAvailableCustomers([]);
            setSelectedCustomer(null);
            setUserAssignedCustomerId(null);
            setIsUserBound(false);
            setDesktopAccessBlocked(false);
            setOperationalRoles([]);
            setOperationalAssignments([]);
            setActiveRoleState(null);
            setPendingRoleChoice(false);
          }
        }
      } catch (error) {
        logger.error('customer.context', 'Failed to load customers', error);
        // No fallback to mock data - show empty state
        setAvailableCustomers([]);
        setSelectedCustomer(null);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    loadCustomers();
  }, [user, userIsAdmin]);

  const selectCustomer = async (
    customerId: string | null,
    options?: { onComplete?: () => void }
  ): Promise<{ success: boolean; error?: string }> => {
    // Admin can select any customer or null (general view)
    // Non-admin can only select from their assigned customers (no null)
    if (!userIsAdmin) {
      if (customerId === null) {
        logger.warn('customer.context', 'Non-admin users cannot deselect customer');
        return { success: false, error: 'Usuários não-administradores não podem desselecionar cliente' };
      }
      // Verify the customer is in their available list
      const isAssigned = availableCustomers.some(c => c.id === customerId);
      if (!isAssigned) {
        logger.warn('customer.context', 'Non-admin user attempted to select unassigned customer', { customerId });
        return { success: false, error: 'Você não tem permissão para acessar este cliente' };
      }
    }

    if (customerId === null) {
      console.log('[CustomerContext] Setting selectedCustomer to null (general view)');
      setSelectedCustomer(null);
      if (user) clearCustomerSelection(user.id);

      // Invalidate dashboard cache when deselecting customer
      queryClient.invalidateQueries(['dashboard']);

      logger.info('customer.context', 'Deselected customer, switching to general view');

      // Use requestAnimationFrame to ensure state has flushed before callback
      if (options?.onComplete) {
        requestAnimationFrame(() => {
          console.log('[CustomerContext] Callback executing for null selection');
          options.onComplete!();
        });
      }

      console.log('[CustomerContext] selectCustomer returning success (null)');
      return { success: true };
    }

    // Try to find in already loaded customers first
    const existingCustomer = availableCustomers.find(c => c.id === customerId);
    if (existingCustomer) {
      console.log('[CustomerContext] Setting selectedCustomer to:', existingCustomer.name, existingCustomer.id);
      setSelectedCustomer(existingCustomer);
      if (user) saveCustomerSelection(existingCustomer, user.id);

      // Invalidate dashboard cache when selecting customer
      queryClient.invalidateQueries(['dashboard']);

      logger.info('customer.context', 'Selected customer', { customerName: existingCustomer.name });

      // Use double requestAnimationFrame to ensure state has fully propagated
      // This guarantees the state is visible in the component tree before callback runs
      if (options?.onComplete) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            console.log('[CustomerContext] Callback executing, current selectedCustomer:', existingCustomer.name);
            options.onComplete!();
          });
        });
      }

      console.log('[CustomerContext] selectCustomer returning success');
      return { success: true };
    }

    // If not found in available list (admin only), fetch from API
    if (userIsAdmin) {
      try {
        const rawCustomer = await customerService.getCustomerById(customerId);
        if (rawCustomer) {
          // Transform customer to extract condoType from condo_data
          const customer = transformCustomer(rawCustomer);
          setSelectedCustomer(customer);
          if (user) saveCustomerSelection(customer, user.id);

          // Invalidate dashboard cache when selecting customer
          queryClient.invalidateQueries(['dashboard']);

          logger.info('customer.context', 'Selected customer from API', { customerName: customer.name });

          // Use double requestAnimationFrame to ensure state has fully propagated
          if (options?.onComplete) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                options.onComplete!();
              });
            });
          }

          return { success: true };
        } else {
          logger.warn('customer.context', 'Customer not found', { customerId });
          return { success: false, error: 'Cliente não encontrado' };
        }
      } catch (error) {
        logger.error('customer.context', 'Failed to load customer', error);
        return { success: false, error: 'Erro ao carregar cliente' };
      }
    }

    return { success: false, error: 'Erro ao selecionar cliente' };
  };

  const clearSavedSelection = () => {
    if (user) clearCustomerSelection(user.id);
    setSelectedCustomer(null);
  };

  const refreshCustomers = async () => {
    if (!user) return;

    logger.info('customer.context', 'Refreshing customers list');

    try {
      if (userIsAdmin) {
        const response = await customerService.getAllCustomers();
        const rawCustomers = response.data || [];
        const customers = rawCustomers.map(transformCustomer);
        setAvailableCustomers(customers);
        // Also sync selectedCustomer so feature flags are immediately visible
        setSelectedCustomer(prev => {
          if (!prev) return prev;
          const updated = customers.find((c: Customer) => c.id === prev.id);
          return updated ?? prev;
        });
        logger.info('customer.context', 'Refreshed customers for admin', { count: customers.length });
      } else {
        // For non-admin users, re-fetch their assigned customers — but only the ones
        // belonging to the CURRENTLY ACTIVE role (see applyRole/chooseRole). This refresh
        // exists to pick up fresh customer DATA (feature flags, etc.), not to change WHICH
        // customers are in scope — it used to rebuild the list from every active
        // assignment regardless of role, which silently widened a role-filtered
        // availableCustomers back out to ALL of the user's customers the moment
        // OperationalView mounted and called this on load (e.g. reintroducing a
        // sindico-only customer into a porteiro's operational customer switcher).
        const response = await fetchWithAuth(`/api/v1/users/${user.id}/assignments`);
        if (response.ok) {
          const assignmentsData = await response.json();
          const assignments = assignmentsData.data || [];
          const activeAssignments = assignments.filter((a: any) => a.is_active && !NON_OPERATIONAL_ROLES.has(a.role));
          const relevantAssignments = activeRole
            ? activeAssignments.filter((a: any) => a.role === activeRole)
            : activeAssignments;
          const assignedCustomerIds = Array.from(new Set(relevantAssignments.map((a: any) => a.customer_id)));

          if (assignedCustomerIds.length > 0) {
            const allCustomersResponse = await customerService.getAllCustomers();
            const rawAllCustomers = allCustomersResponse.data || [];
            const allCustomers = rawAllCustomers.map(transformCustomer);
            const assignedCustomers = allCustomers.filter((c: Customer) =>
              assignedCustomerIds.includes(c.id)
            );
            setAvailableCustomers(assignedCustomers);
            // Also sync selectedCustomer
            setSelectedCustomer(prev => {
              if (!prev) return prev;
              const updated = assignedCustomers.find((c: Customer) => c.id === prev.id);
              return updated ?? prev;
            });
            logger.info('customer.context', 'Refreshed customers for non-admin', { count: assignedCustomers.length });
          }
        }
      }
    } catch (error) {
      logger.error('customer.context', 'Failed to refresh customers', error);
    }
  };

  return (
    <CustomerContext.Provider
      value={{
        selectedCustomer,
        availableCustomers,
        selectCustomer,
        clearSavedSelection,
        refreshCustomers,
        userAssignedCustomerId,
        isUserBound,
        isLoading,
        isInitialized,
        desktopAccessBlocked,
        operationalRoles,
        activeRole,
        pendingRoleChoice,
        chooseRole,
        openRolePicker,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
}
